import {nCols, nRows} from "~/components/reports/reportConstants";
import type {
    DndGridOccupancyMatrix,
    EditableReportPage,
} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-types";
import {ResizeHandlePosition} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_reportScaffold/report/edit/$reportSlug/-types";

export const convertZeroBasedIndexToRowCol = (
    index: number,
    totalCols: number,
) => ({
    row: Math.floor(index / totalCols),
    col: index % totalCols,
});

export const convertRowColToZeroBasedIndex = (
    row: number,
    col: number,
    totalCols: number,
) => row * totalCols + col;

export function getAlignmentClassesForResizeHandles(
    handlePosition: ResizeHandlePosition,
): {containerClasses: string; lineClasses: string} {
    switch (handlePosition) {
        case ResizeHandlePosition.east: {
            return {
                containerClasses: "right-0 cursor-col-resize",
                lineClasses:
                    "border-r-[2px] justify-self-end align-self-center h-2/3 !w-1",
            };
        }
        case ResizeHandlePosition.west: {
            return {
                containerClasses: "left-0 cursor-col-resize",
                lineClasses:
                    "border-l-[2px] justify-self-start align-self-center h-2/3 !w-1",
            };
        }

        case ResizeHandlePosition.north: {
            return {
                containerClasses: "top-0 cursor-row-resize",
                lineClasses:
                    "border-t-[2px] justify-self-center align-self-start w-2/3 !h-1",
            };
        }

        case ResizeHandlePosition.south: {
            return {
                containerClasses: "bottom-0 cursor-row-resize",
                lineClasses:
                    "border-b-[2px] justify-self-center self-end w-2/3 !h-1",
            };
        }

        case ResizeHandlePosition.northWest: {
            return {
                containerClasses: "top-0 left-0 cursor-nwse-resize rotate-90",
                lineClasses: "border-l-[2px] border-b-[2px] rounded-bl-md",
            };
        }

        case ResizeHandlePosition.northEast: {
            return {
                containerClasses: "top-0 right-0 cursor-nesw-resize rotate-180",
                lineClasses: "border-l-[2px] border-b-[2px] rounded-bl-md",
            };
        }

        case ResizeHandlePosition.southWest: {
            return {
                containerClasses: "bottom-0 left-0 cursor-nesw-resize",
                lineClasses: "border-l-[2px] border-b-[2px] rounded-bl-md",
            };
        }
        case ResizeHandlePosition.southEast: {
            return {
                containerClasses:
                    "bottom-0 right-0 cursor-nwse-resize -rotate-90",
                lineClasses: "border-l-[2px] border-b-[2px] rounded-bl-md",
            };
        }

        default: {
            throw new Error("420b8eb0-8f82-4b0a-8a40-5d351986a081");
        }
    }
}

export function getDimensionsForResizeHandles(
    handlePosition: ResizeHandlePosition,
    thickness: number,
    cornerLength: number,
    edgeLength: number,
): {height: number; width: number} {
    switch (handlePosition) {
        case ResizeHandlePosition.east: {
            return {
                height: edgeLength,
                width: thickness,
            };
        }
        case ResizeHandlePosition.west: {
            return {
                height: edgeLength,
                width: thickness,
            };
        }

        case ResizeHandlePosition.north: {
            return {
                height: thickness,
                width: edgeLength,
            };
        }

        case ResizeHandlePosition.south: {
            return {
                height: thickness,
                width: edgeLength,
            };
        }

        case ResizeHandlePosition.northWest: {
            return {
                height: cornerLength,
                width: cornerLength,
            };
        }

        case ResizeHandlePosition.northEast: {
            return {
                height: cornerLength,
                width: cornerLength,
            };
        }

        case ResizeHandlePosition.southWest: {
            return {
                height: cornerLength,
                width: cornerLength,
            };
        }
        case ResizeHandlePosition.southEast: {
            return {
                height: cornerLength,
                width: cornerLength,
            };
        }

        default: {
            throw new Error("220f3efb-b735-410c-b626-263205d1c3ed");
        }
    }
}

export function calculateGridDerivedState(
    pages: Array<EditableReportPage>,
): Array<DndGridOccupancyMatrix> {
    const gridCellToOccupancies: Array<DndGridOccupancyMatrix> = [];

    for (const page of pages) {
        const gridCellToOccupancy: DndGridOccupancyMatrix = Array.from(
            {length: nRows},
            () => Array(nCols).fill(null),
        );

        for (const [widgetIndex, widget] of page.widgets.entries()) {
            for (let col = widget.colStart; col < widget.colEnd; col++) {
                for (let row = widget.rowStart; row < widget.rowEnd; row++) {
                    if (gridCellToOccupancy[row] != null) {
                        gridCellToOccupancy[row][col] = widgetIndex;
                    }
                }
            }
        }

        gridCellToOccupancies.push(gridCellToOccupancy);
    }

    return gridCellToOccupancies;
}
